import api from './api';
import { DisplayMode } from '../models/project';
import { ProvidedAsset } from './projectAssets';


export interface ProjectResponse {
  id: string;
  name: string;
  version: string;
  creditsUsed?: number;
  totalGeneratedImages?: number;
  supportedDisplayModes?: DisplayMode[];
  thumbnailUrl?: string;
  organizeAfterBuild?: boolean;
  engineUpgradeRequired: boolean;
}

export interface CreateProjectPipelineRequest {
  projectName: string;
  prompt: string;
  projectType: 'TwoD' | 'ThreeD';
  displayMode: DisplayMode;
  providedAssets?: ProvidedAsset[];
}


export async function getProject(id: string): Promise<ProjectResponse> {
  const { data } = await api.get<ProjectResponse>(`project/${id}`);
  return data;
}

export interface UpdateProjectRequest {
  name: string;
  organizeAfterBuild?: boolean;
}

export async function updateProject(id: string, request: UpdateProjectRequest): Promise<ProjectResponse> {
  const { data } = await api.put<ProjectResponse>(`project/${id}`, request);
  return data;
}

export async function deleteProject(id: string): Promise<void> {
  await api.delete(`project/${id}`);
}

export async function createProject(request: CreateProjectPipelineRequest): Promise<ProjectResponse> {
  const { data } = await api.post<ProjectResponse>('project', request);
  return data;
}

export interface BuildProjectRequest {
  buildPlatform: 'Android' | 'Ios' | 'WebGL' | 'Windows' | 'Mac' | 'Linux' | 'PlayStation' | 'Xbox' | 'Switch';
}

export async function buildProject(id: string, request: BuildProjectRequest): Promise<void> {
  await api.post(`project/${id}/build`, request);
}

export async function cancelProject(id: string): Promise<{ jobId: string }> {
  const { data } = await api.post<{ jobId: string }>(`project/${id}/cancel`);
  return data;
}

export type PipelineJobType = 'Create' | 'Build' | 'Organize';

export interface ActiveJob {
  jobId: string;
  jobType: PipelineJobType;
}

interface ActiveJobResponse {
  running: boolean;
  jobId: string | null;
  jobType: PipelineJobType | null;
}

const activeJobReads = new Map<string, Promise<ActiveJob | null>>();

/**
 * The project's job a worker is processing, or else its most recently queued one; null when it has none. Callers
 * that ask for the same project while a read is in flight share it.
 */
export function getActiveJob(projectId: string): Promise<ActiveJob | null> {
  let read = activeJobReads.get(projectId);
  if (!read) {
    read = api
      .get<ActiveJobResponse>(`project/${projectId}/jobs/active`)
      .then(({ data }) => (data.jobId && data.jobType ? { jobId: data.jobId, jobType: data.jobType } : null))
      .finally(() => activeJobReads.delete(projectId));
    activeJobReads.set(projectId, read);
  }
  return read;
}
